import { z } from 'zod';
import { resolveDownloadUrl } from 'utils/resolveDownloadUrl';

// Champ d'affichage : son absence dégrade la présentation, elle ne doit pas faire échouer la page.
const displayField = z.string().catch('');

// Un élément indexé se désigne par sa source et son identifiant **chez cette source**
// (docs/discovery/RESTAPI-DISCOVERY.md §2.2) : deux sources ne se concertent pas pour attribuer leurs
// identifiants, un favori qui ne porterait que l'identifiant désignerait deux éléments à la fois.
export type IndexedItemRef = { source: string; entityId: string };

// Noms des sources tels que discovery les inscrit dans les favoris et les récents.
export const INDEX_SOURCES = { applicationForm: 'applicationform', dataRepo: 'datarepo' } as const;

export const indexedItemKey = ({ source, entityId }: IndexedItemRef) => `${source}/${entityId}`;

// DocumentIndexResponse : onze chaînes, qu'un résultat de recherche et l'`item` d'un favori ou d'un
// récent partagent. `category` et `provider` portent des **clés** de classification (« 5 »), pas des
// libellés : la résolution se fait à l'affichage.
const documentIndexFields = {
  title: displayField,
  filename: displayField,
  version: displayField,
  category: displayField,
  provider: displayField,
  section: displayField,
  language: displayField,
  datasource: displayField,
  documentType: displayField,
  downloadUrl: displayField,
};

// Dans l'index, `downloadUrl` est « ce qu'il faut suivre » : la route du fichier chez sa source, ou la
// destination externe d'un document qui pointe ailleurs (RESTAPI-DATAREPO.md §2 « Un document peut
// pointer ailleurs »). On le sépare comme l'API de datarepo sépare `downloadUrl` et `linkUrl`, pour que
// les composants n'aient qu'une règle : un fichier se télécharge, un lien s'ouvre dans une nouvelle page.
// Seul un lien http(s) est gardé, puisqu'il est ouvert tel quel.
const SERVICE_FILE_ROUTE = /\/(?:file|asset)\/[^/?#]+\/content\/[^/?#]+$/;
const EXTERNAL_LINK = /^https?:\/\//i;

const splitFollowUrl = (url: string) => {
  const resolved = resolveDownloadUrl(url);
  if (!resolved || SERVICE_FILE_ROUTE.test(resolved)) return { downloadUrl: resolved, linkUrl: '' };
  return { downloadUrl: '', linkUrl: EXTERNAL_LINK.test(resolved) ? resolved : '' };
};

// Le favori et le récent ne recopient rien : `item` est relu dans l'index. Les composants n'en
// affichent que la présentation, normalisée ici avec la référence qui sert à le retirer.
const indexedLinkResponseSchema = z.object({
  source: z.string().min(1),
  entityId: z.string().min(1),
  item: z.object(documentIndexFields),
});

const toDocumentLink = ({ source, entityId, item }: z.infer<typeof indexedLinkResponseSchema>) => ({
  source,
  entityId,
  title: item.title,
  filename: item.filename,
  documentType: item.documentType,
  ...splitFollowUrl(item.downloadUrl),
});

export const favoriteDocumentSchema = indexedLinkResponseSchema
  .extend({ createdDateTimeUtc: z.string() })
  .transform(response => ({ ...toDocumentLink(response), createdDateTimeUtc: response.createdDateTimeUtc }));

// La doc annonce « mêmes champs que FavoriteResponse », mais l'ancien service ne rendait pas la date
// d'ajout sur un récent et la phrase n'a pas changé : l'exiger rejetterait toute la liste, et
// l'encadré « Récemment consultés » resterait vide sans la moindre erreur.
export const recentDocumentSchema = indexedLinkResponseSchema
  .extend({
    lastViewDateTimeUtc: z.string(),
    touchCount: z.number(),
  })
  .transform(response => ({
    ...toDocumentLink(response),
    lastViewDateTimeUtc: response.lastViewDateTimeUtc,
    touchCount: response.touchCount,
  }));

// Un résultat de recherche ne porte ni sa source ni son `entityId` (RESTAPI-DISCOVERY.md §3), et
// l'onglet « Site » mêle applicationform et datarepo, qui publient tous deux `Datasource = Site`.
// Seul datarepo publie des documents qui pointent ailleurs, et chaque source compose la route de ses
// fichiers sous son propre segment de passerelle : le lien suffit à les départager. L'`entityId` est
// l'identifiant public du document chez l'une comme chez l'autre (RESTAPI-DISCOVERY.md §2.2).
const DATAREPO_FILE_ROUTE = /\/datarepo\/(?:api\/)?(?:file|asset)\//;

const sourceOfResult = ({ downloadUrl, linkUrl }: { downloadUrl: string; linkUrl: string }) =>
  linkUrl || DATAREPO_FILE_ROUTE.test(downloadUrl) ? INDEX_SOURCES.dataRepo : INDEX_SOURCES.applicationForm;

export const searchDocumentSchema = z.object({ publicId: z.guid(), ...documentIndexFields }).transform(result => {
  const followUrls = splitFollowUrl(result.downloadUrl);
  return {
    ...result,
    ...followUrls,
    source: sourceOfResult(followUrls),
    entityId: result.publicId,
  };
});

export const searchResponseSchema = z.object({
  // Décompte **avant déduplication** : une estimation de volume, jamais un nombre de pages.
  totalResult: z.number().catch(0),
  // Un résultat malformé — identifiant absent ou invalide — ne désigne aucun document : on l'écarte
  // seul. Poser le `.catch([])` sur le tableau entier viderait la page au premier résultat fautif,
  // et une page vide est exactement ce que rend un index vide : l'erreur passerait inaperçue.
  results: z
    .array(z.unknown())
    .catch([])
    .transform(rows =>
      rows.flatMap(row => {
        const parsed = searchDocumentSchema.safeParse(row);
        return parsed.success ? [parsed.data] : [];
      }),
    ),
});

// Le lien commun aux favoris et aux récents : ce que les composants de liste affichent.
export type DocumentLink = ReturnType<typeof toDocumentLink>;
export type RecentDocument = z.infer<typeof recentDocumentSchema>;
export type SearchDocument = z.infer<typeof searchDocumentSchema>;
export type SearchResponse = z.infer<typeof searchResponseSchema>;
